<template>
  <div class="horizontal layout center font-normal comment-inside-box">
    <div
      v-if="messages.length > 1"
      @click="toggleOpenComment()"
      class="cursor-pointer label-box-comment"
    >
      {{ messages.length }} {{ $i18nPresenter.t('comments') }}
    </div>
    <div
      v-if="messages.length === 1"
      @click="toggleOpenComment()"
      class="cursor-pointer label-box-comment"
    >
      {{ messages.length }} {{ $i18nPresenter.t('comment') }}
    </div>
    <div class="input-comment" :class="{'error': textComment.length > 200 }">
      <aha-input
        type="text"
        :placeholder="$i18nPresenter.t('Add a comment...')"
        @keyup.enter="sendComment()"
        v-model="textComment"
        @focus="$store.state.isFocusInputCommentPresentation = true"
        @blur="$store.state.isFocusInputCommentPresentation = false"
        :maxLength="200"
      />
      <i
        class="fas fa-paper-plane"
        @click.stop="sendComment()"
        :class="{'active': textComment}"
      ></i>
      <div class="list-comments" v-if="isOpenComment">
        <i class="fas fa-times" @click="toggleOpenComment()"></i>
        <ul ref="scroll-comment">
          <li class="horizontal layout" v-for="(item, index) in messages" :key="index">
            <div class="font-bold mr-2" v-if="item.user && item.user.id">
              {{ item.user.name }} <span v-if="item.user.id === currentUser.id">{{ $i18nPresenter.t('(You)') }}</span>
            </div>
            <div class="font-bold mr-2" v-if="!item.user || !item.user.id">
              {{ $i18nPresenter.t('Anonymous') }}
            </div>
            <div class="flex">
              {{ item.message }}
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import commentApi from '@/api/comment'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import Util from '@/util'

export default {
  components: {
  },
  props: {
    uniqueAccessCode: {
      type: String,
      required: true,
    },
    presentationId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      textComment: '',
      messages: [],
      isOpenComment: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
  },
  mounted() {
    this.getComments()
    EventBus.$on('presentation-new-comment', (comment) => {
      this.messages.push(comment)
    })
  },
  destroyed() {
    EventBus.$off('presentation-new-comment')
  },
  methods: {
    toggleOpenComment() {
      this.isOpenComment = !this.isOpenComment
      if (this.isOpenComment) {
        setTimeout(() => {
          this.scrollToElement(true)
        }, 200)
      }
    },
    async getComments() {
      const resp = await commentApi.getComments(this.presentationId, this.uniqueAccessCode)
      if (resp && resp.data) {
        this.messages = resp.data.reverse()
      }
    },
    async sendComment() {
      if (!this.textComment) return
      if (this.textComment.length > 200) return
      const text = this.textComment.trim()
      if (!text) return
      const payload = {
        presentationId: this.presentationId,
        uniqueAccessCode: this.uniqueAccessCode,
        message: text,
        socketId: Socket.getSocketId(),
        userId: this.currentUser.id,
        userName: this.currentUser.firstName,
        email: this.currentUser.email,
      }
      const data = JSON.parse(JSON.stringify(payload))
      if (this.currentUser.id) {
        data.user = {
          email: this.currentUser.email,
          name: this.currentUser.firstName,
          id: this.currentUser.id,
        }
        data.createdAt = new Date()
      }
      this.messages.push(data)
      setTimeout(() => {
        this.scrollToElement()
      }, 100)
      this.textComment = ''
      await commentApi.sendComment(payload)
    },
    scrollToElement(isNoAnimate) {
      const el = this.$refs['scroll-comment']
      if (el) {
        let behavior = 'smooth'
        if (isNoAnimate) {
          behavior = 'instant'
        }
        // Use el.scrollIntoView() to instantly scroll to the element
        Util.scrollToElement(el, {
          behavior,
          top: el.scrollHeight,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.input-comment {
  position: relative;
  margin-left: 16px;
  &::v-deep {
    .aha-input--char-counter {
      position: absolute;
      right: 47px;
      top: 6px;
      font-size: 14px;
    }
    input {
      height: 49px;
      background: #f1f1f1;
      border: 1px solid #B4BCCF;
      border-radius: 39px;
      width: 360px;
      padding: 0 16px;
      color: #313131;
      padding-right: 78px;
      &:focus {
        border-color: $primary-color;
      }
    }

    i {
      position: absolute;
      top: -6px;
      color: #AFAFAF;
      font-size: 20px;
      padding: 20px;
      right: 0px;
      cursor: pointer;

      &:hover, &.active{
        @apply text-primary-90;
      }
    }

    &:hover {
      input {
        border-color: $primary-color;
      }
    }
  }
}

.list-comments {
  position: absolute;
  background-color: #fff;
  color: #313131;
  bottom: 60px;
  width: 100%;
  border: 1px solid #D1D1D1;
  border-radius: 12px;

  ul {
    max-height: 260px;
    padding: 16px;
    margin: 0;
    overflow-y: auto;
    overflow-x: hidden;

    li {
      border-bottom: 1px solid #EBEBEB;
      padding-bottom: 12px;
      margin-bottom: 12px;

      &:last-child {
        border: none;
        margin-bottom: 0;
        padding-bottom: 0;
      }

      .flex {
        word-break: break-word;
      }
    }
  }

  i.fa-times {
    top: -13px;
    right: -6px;
  }
}

@media (max-width: 1024px) {
  .comment-inside-box {
    justify-content: center;
  }
}

@media (max-width: 560px) {
  .label-box-comment {
    position: absolute;
    left: 16px;
    margin-bottom: -80px;
  }

  .input-comment {
    width: 100%;
    margin-left: 0;
    &::v-deep {
      input {
        width: 100%;
      }
    }
  }
  .comment-inside-box {
    padding-bottom: 32px !important;
  }
}

.count-text {
  position: absolute;
  right: 47px;
  top: 14px;
  font-size: 14px;
  color: #AFAFAF;
}

.input-comment {
  &::v-deep {
    &.error {
      input {
        border-color: #ff0000;
      }

      .count-text {
        color: #ff0000;
      }
    }
  }
}

.label-box-comment {
  &:hover {
    @apply text-primary-90;
  }
}
</style>
